import { hexDist, hexLen, type Hex } from '../hex.ts';
import { idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng, type RngState } from '../math/rng.ts';

/**
 * The Burn's glass shore (docs/sendas/paladin.md, chapter 1; §4, «Los canales»): a tongue of black glass going into the
 * sea, the sea along its far rim and both flanks, the Burn inland behind the near rim. The first Vigil cut fine canals
 * in the glass from the sea down to the altars' pools: each wave runs down the inlet, and at every fork a stone shuts
 * one mouth and leaves the other. A mouth that isn't the way on turns back to the sea at the flank.
 *
 * - `small` (act 1): three forks one after another, all three on the water's way, and one pool with the first altar
 *   beside it. Each fork sends the wave on or back to the sea, so one setting of the three stones (of eight) reaches the
 *   pool. A fork is drawn one of two ways: the way on goes straight down and the way back turns off to the flank; or
 *   the way on swings out wide before it comes back in, and the way back starts down toward the altar and only then
 *   hooks off to the sea (the obvious mouth is the wrong one). It is read by following the canals, not by trying.
 * - `ebb` (act 3, the test): the inlet's fork (the way on, or back to the sea), then the fork between the low pool (the
 *   first altar's, down the middle) and the canal that climbs to the high one (the second eye's, cut in a rock on one
 *   flank). The climb's last cell is the high pool's lip, where its own stone opens or shuts its mouth.
 *
 * Integers only, and nothing but the seed: the server builds the same one. Rows run from the sea (r = −R) to the Burn
 * (r = R); x in half hexes is 2q + r.
 */

export const CANAL_RADIUS = 7;
const R = CANAL_RADIUS;
/** Rim cells at this row or above border the sea (the flanks); below, the Burn's glass goes on inland. */
export const CANAL_SEA_ROW = 3;

export interface CanalEdge {
  /** The canal's cells after its start, its last the place it runs into (a fork, a pool, the sea at the rim). */
  cells: Hex[];
  to: { kind: 'stone' | 'pool' | 'sea'; n: number };
  /** The canal that climbs to the high pool: only a great wave gets up it. */
  climb?: true;
}

export interface CanalStone {
  /** Its cell (a fork's, or the high pool's lip). */
  at: Hex;
  /** A fork: the two canals out of it, the one with the lower x first (position 0 sends the water down it). */
  out: [number, number];
  /** The high pool's mouth instead (its pool): position 0 open, 1 shut. */
  mouth?: number;
}

export interface CanalPool {
  at: Hex;
  /** The altar beside it (its eye opens with the pool full). */
  altar: Hex;
  high: boolean;
  /** The stone at its mouth (−1: none). */
  mouth: number;
}

export interface CanalLayout {
  kind: 'small' | 'ebb';
  /** Nothing walks: the sea, the glass beyond the board, the altars, the high pool's rock. */
  rocks: Hex[];
  /** Of those, the sea's (the render's water). */
  sea: Hex[];
  /** The high pool's rock (the render raises it). */
  crag: Hex[];
  edges: CanalEdge[];
  inlet: number;
  stones: CanalStone[];
  pools: CanalPool[];
  /** Each fork's good position (the small one: all three; the test: the inlet's fork, the others by the phase). */
  answer: number[];
  /** The stones as the fight starts. */
  start: number[];
  /** The glass the sea wets at the rim: always water. */
  surf: Hex[];
  /** Where Ser Marit keeps and where the hero starts. */
  marit: Hex;
  hero: Hex;
  /** The Burn's rim, where what comes from inland comes in. */
  spawn: Hex[];
}

const key = (h: Hex): string => `${h.q},${h.r}`;
const x2 = (h: Hex): number => 2 * h.q + h.r;

/** The cell at row `r` whose x (half hexes) is `x`, or the next one toward `bias` when the parity is off. */
const at = (r: number, x: number, bias = 1): Hex => {
  const xx = ((x - r) & 1) === 0 ? x : x + (bias >= 0 ? 1 : -1);
  return { q: idiv(xx - r, 2), r };
};

/** The rim's cell at row `r` on a flank (`side` +1 east, −1 west). */
const rimAt = (r: number, side: number): Hex => (side > 0 ? { q: imin(R, R - r), r } : { q: imax(-R, -R - r), r });

/** Integer division rounding down (toward −∞). */
const floorDiv = (a: number, b: number): number => {
  const q = idiv(a, b);
  return q * b > a ? q - 1 : q;
};

/** Cube rounding of a point given in axial thousandths (integers only). */
const round = (qf: number, rf: number): Hex => {
  const sf = -qf - rf;
  const rd = (v: number): number => floorDiv(v + 500, 1000);
  let q = rd(qf);
  let r = rd(rf);
  const s = rd(sf);
  const dq = Math.abs(q * 1000 - qf);
  const dr = Math.abs(r * 1000 - rf);
  const ds = Math.abs(s * 1000 - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return { q, r };
};

/** The hexes from `a` to `b` (both in), a straight line on the grid. */
export const hexLine = (a: Hex, b: Hex): Hex[] => {
  const n = hexDist(a, b);
  if (n === 0) return [a];
  const out: Hex[] = [];
  for (let i = 0; i <= n; i++) {
    // A nudge off the edges between hexes, always the same way.
    const qf = floorDiv((a.q * (n - i) + b.q * i) * 1000, n) + 1;
    const rf = floorDiv((a.r * (n - i) + b.r * i) * 1000, n) + 2;
    out.push(round(qf, rf));
  }
  return out;
};

/** A canal through these points: each leg's line, the joints once, the first point left out (it is the canal's start). */
const run = (pts: Hex[]): Hex[] => {
  const out: Hex[] = [];
  for (let i = 1; i < pts.length; i++) {
    const leg = hexLine(pts[i - 1] as Hex, pts[i] as Hex);
    for (let k = 1; k < leg.length; k++) out.push(leg[k] as Hex);
  }
  return out;
};

const sideOf = (rng: RngState): number => (rngInt(rng, 2) === 0 ? -1 : 1);

/**
 * A fork at `x` sending the water on to `y` (good) or back to the sea on the other flank. `style` 0: the way on straight
 * down, the way back off to the flank and up; 1: the way on swinging out wide to `g`'s flank, the way back first down
 * toward the altar, then off to the sea on the other flank.
 */
const fork = (from: Hex, to: Hex, g: number, style: number): { good: Hex[]; bad: Hex[] } => {
  const dy = to.r - from.r;
  if (style === 0) {
    const b = -g;
    const mid = at(from.r, x2(from) + b * 5, b);
    return { good: run([from, to]), bad: run([from, mid, rimAt(from.r - 1, b)]) };
  }
  void dy;
  // Out wide and down to the next one's row, then in along it; the way back first down a step toward the altar.
  const swing = at(to.r, x2(to) + g * 6, g);
  const decoy = at(from.r + 2, x2(from) - g, -g);
  const back = rimAt(imin(CANAL_SEA_ROW, from.r + 1), -g);
  return { good: run([from, swing, to]), bad: run([from, decoy, back]) };
};

/** The two canals out of a stone, the lower x first (position 0 sends the water down the first). */
const order = (edges: CanalEdge[], a: number, b: number): [number, number] => {
  const ea = (edges[a] as CanalEdge).cells[0] as Hex;
  const eb = (edges[b] as CanalEdge).cells[0] as Hex;
  return x2(ea) <= x2(eb) ? [a, b] : [b, a];
};

/** No two canals share a cell or run side by side (but where they leave the same fork). */
const clear = (edges: CanalEdge[], nodes: Hex[]): boolean => {
  const own = new Map<string, number>();
  for (let i = 0; i < edges.length; i++)
    for (const c of (edges[i] as CanalEdge).cells) {
      if (hexLen(c.q, c.r) > R) return false;
      const k = key(c);
      if (own.has(k)) return false;
      own.set(k, i);
    }
  const near = (c: Hex): boolean => nodes.some((n) => hexDist(n, c) <= 1);
  for (let i = 0; i < edges.length; i++)
    for (const c of (edges[i] as CanalEdge).cells) {
      if (near(c)) continue;
      for (let j = 0; j < edges.length; j++) {
        if (j === i) continue;
        for (const d of (edges[j] as CanalEdge).cells) if (!near(d) && hexDist(c, d) <= 1) return false;
      }
    }
  return true;
};

/** The ring of cells round the board (hexLen R+1 and R+2), the sea's where they border it. */
const beyond = (): { rocks: Hex[]; sea: Hex[] } => {
  const rocks: Hex[] = [];
  const sea: Hex[] = [];
  for (let r = -R - 2; r <= R + 2; r++)
    for (let q = -R - 2; q <= R + 2; q++) {
      const d = hexLen(q, r);
      if (d <= R || d > R + 2) continue;
      rocks.push({ q, r });
      if (r <= CANAL_SEA_ROW) sea.push({ q, r });
    }
  return { rocks, sea };
};

const surfOf = (): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= CANAL_SEA_ROW; r++)
    for (let q = -R; q <= R; q++) if (hexLen(q, r) === R) out.push({ q, r });
  return out;
};

const spawnOf = (): Hex[] => {
  const out: Hex[] = [];
  for (let r = 5; r <= R; r++)
    for (let q = -R; q <= R; q++) if (hexLen(q, r) === R) out.push({ q, r });
  return out;
};

/** Starting stones: as many as `n`, at least two of them wrong (the fight never opens solved). */
const startOf = (rng: RngState, answer: number[]): number[] => {
  const pos = answer.map((a) => a);
  const k = rngInt(rng, answer.length);
  for (let i = 0; i < answer.length; i++) if (i !== k) pos[i] = 1 - (answer[i] as number);
  return pos;
};

const small = (seed: number): CanalLayout | null => {
  const rng = seedRng(deriveSeed(seed, 0xca1a));
  const rows = [-5, -2, 1];
  const forks = rows.map((r) => at(r, rngInt(rng, 3) - 1, sideOf(rng)));
  const pool = at(4, rngInt(rng, 3) - 1, sideOf(rng));
  const altar = { q: pool.q, r: pool.r + 1 };
  const inletAt = at(-R, x2(forks[0] as Hex) + (rngInt(rng, 3) - 1) * 2, 1);
  if (hexLen(inletAt.q, inletAt.r) !== R) return null;
  const edges: CanalEdge[] = [{ cells: [inletAt, ...run([inletAt, forks[0] as Hex])], to: { kind: 'stone', n: 0 } }];
  const stones: CanalStone[] = [];
  const answer: number[] = [];
  for (let i = 0; i < 3; i++) {
    const from = forks[i] as Hex;
    const to = i < 2 ? (forks[i + 1] as Hex) : pool;
    const f = fork(from, to, sideOf(rng), rngInt(rng, 10) < 7 ? 1 : 0);
    const good = edges.length;
    edges.push({ cells: f.good, to: i < 2 ? { kind: 'stone', n: i + 1 } : { kind: 'pool', n: 0 } });
    const bad = edges.length;
    edges.push({ cells: f.bad, to: { kind: 'sea', n: 0 } });
    const out = order(edges, good, bad);
    stones.push({ at: from, out });
    answer.push(out[0] === good ? 0 : 1);
  }
  if (!clear(edges, [...forks, pool])) return null;
  const b = beyond();
  // Ser Marit keeps by the altar, on the side away from where the last canal comes in; the hero starts by her.
  const last = (edges[(stones[2] as CanalStone).out[answer[2] as number] as number] as CanalEdge).cells;
  const inSide = x2(last[last.length - 2] ?? pool) <= x2(pool) ? -1 : 1;
  const marit = at(pool.r, x2(pool) - inSide * 4, -inSide);
  const hero = at(pool.r - 1, x2(pool) - inSide * 6, -inSide);
  if ([marit, hero].some((h) => edges.some((e) => e.cells.some((c) => c.q === h.q && c.r === h.r)))) return null;
  return {
    kind: 'small',
    rocks: [...b.rocks, altar],
    sea: b.sea,
    crag: [],
    edges,
    inlet: 0,
    stones,
    pools: [{ at: pool, altar, high: false, mouth: -1 }],
    answer,
    start: startOf(rng, answer),
    surf: surfOf(),
    marit,
    hero,
    spawn: spawnOf(),
  };
};

const ebb = (seed: number): CanalLayout | null => {
  const rng = seedRng(deriveSeed(seed, 0xebb0));
  const a = at(-5, rngInt(rng, 3) - 1, sideOf(rng));
  const f = at(-2, rngInt(rng, 3) - 1, sideOf(rng));
  const low = at(4, rngInt(rng, 3) - 1, sideOf(rng));
  const lowAltar = { q: low.q, r: low.r + 1 };
  // The high pool in a rock on one flank, its eye beside it, a little nearer the sea than the fork's row below.
  const h = sideOf(rng);
  const high = at(rngInt(rng, 2), h * (8 + rngInt(rng, 2) * 2), h);
  const highAltar = at(high.r, x2(high) + h * 2, h);
  const inletAt = at(-R, x2(a) + (rngInt(rng, 3) - 1) * 2, 1);
  if (hexLen(inletAt.q, inletAt.r) !== R || hexLen(highAltar.q, highAltar.r) >= R) return null;
  const edges: CanalEdge[] = [{ cells: [inletAt, ...run([inletAt, a])], to: { kind: 'stone', n: 0 } }];
  const fa = fork(a, f, sideOf(rng), rngInt(rng, 2));
  edges.push({ cells: fa.good, to: { kind: 'stone', n: 1 } }, { cells: fa.bad, to: { kind: 'sea', n: 0 } });
  const outA = order(edges, 1, 2);
  // The fork between the pools: down the middle to the low one (straight, or swinging out to the other flank), and
  // up to the high one; the climb's last cell is the lip, the mouth's stone.
  const swing = rngInt(rng, 2) === 1;
  const lowWay = swing ? run([f, at(1, x2(f) - h * 5, -h), low]) : run([f, low]);
  const climb = run([f, at(high.r - 1, x2(high) - h * 2, -h), high]);
  if (climb.length < 4) return null;
  const lip = climb[climb.length - 2] as Hex;
  const lowE = edges.length;
  edges.push({ cells: lowWay, to: { kind: 'pool', n: 0 } });
  const highE = edges.length;
  edges.push({ cells: climb.slice(0, climb.length - 1), to: { kind: 'stone', n: 2 }, climb: true });
  const into = edges.length;
  edges.push({ cells: [high], to: { kind: 'pool', n: 1 }, climb: true });
  const outF = order(edges, lowE, highE);
  const stones: CanalStone[] = [
    { at: a, out: outA },
    { at: f, out: outF },
    { at: lip, out: [into, into], mouth: 1 },
  ];
  if (!clear(edges, [a, f, lip, high, low])) return null;
  // The rock round the high pool: its ring, but the lip and a step to it on the board's side (the hero reaches the
  // mouth's stone from there).
  const ring: Hex[] = [];
  for (const d of [
    { q: 1, r: 0 },
    { q: 1, r: -1 },
    { q: 0, r: -1 },
    { q: -1, r: 0 },
    { q: -1, r: 1 },
    { q: 0, r: 1 },
  ]) {
    const c = { q: high.q + d.q, r: high.r + d.r };
    if (key(c) === key(lip) || hexLen(c.q, c.r) > R) continue;
    if (edges.some((e) => e.cells.some((x) => key(x) === key(c)))) continue;
    ring.push(c);
  }
  // Keep one step to the lip open from the board's side.
  const step = ring.filter((c) => hexDist(c, lip) === 1).sort((p, q) => Math.abs(x2(p)) - Math.abs(x2(q)) || p.r - q.r)[0];
  const crag = [high, highAltar, ...ring.filter((c) => !step || key(c) !== key(step))];
  const b = beyond();
  // Ser Marit keeps between the two pools; the hero starts by her.
  const marit = at(2, h * 4, h);
  const hero = at(2, h * 2, -h);
  const taken = (c: Hex): boolean => edges.some((e) => e.cells.some((x) => key(x) === key(c))) || crag.some((x) => key(x) === key(c));
  if (taken(marit) || taken(hero) || key(lowAltar) === key(marit)) return null;
  const answerA = outA[0] === 1 ? 0 : 1;
  return {
    kind: 'ebb',
    rocks: [...b.rocks, lowAltar, ...crag],
    sea: b.sea,
    crag,
    edges,
    inlet: 0,
    stones,
    pools: [
      { at: low, altar: lowAltar, high: false, mouth: -1 },
      { at: high, altar: highAltar, high: true, mouth: 2 },
    ],
    // The inlet's fork has one good way; the pools' fork and the mouth go by the phase.
    answer: [answerA, outF[0] === lowE ? 0 : 1, 1],
    // The inlet's stone wrong, the pools' fork down to the low one, the mouth open: as the water left them.
    start: [1 - answerA, outF[0] === lowE ? 0 : 1, 0],
    surf: surfOf(),
    marit,
    hero,
    spawn: spawnOf(),
  };
};

/** A plain layout that always fits (when no seed's draw does, after many tries). */
const fallback = (kind: 'small' | 'ebb'): CanalLayout => {
  for (let k = 1; k < 4000; k++) {
    const L = kind === 'small' ? small(k * 7919) : ebb(k * 7919);
    if (L) return L;
  }
  throw new Error('no canal layout');
};

const CACHE = new Map<string, CanalLayout>();

/** The shore's canals for a fight's seed: the small one (act 1) or the test's (act 3). */
export const canalLayout = (seed: number, kind: 'small' | 'ebb'): CanalLayout => {
  const k = `${kind}:${seed}`;
  const hit = CACHE.get(k);
  if (hit) return hit;
  let L: CanalLayout | null = null;
  for (let i = 0; i < 60 && !L; i++) L = kind === 'small' ? small(deriveSeed(seed, i)) : ebb(deriveSeed(seed, i));
  const out = L ?? fallback(kind);
  if (CACHE.size > 64) CACHE.clear();
  CACHE.set(k, out);
  return out;
};

/**
 * Where a wave goes with the stones as they are (the small shore: the pool, or back to the sea): each fork's way by its
 * stone, from the inlet on. For the tests and the bot's checks; the fight runs it a cell at a time.
 */
export const canalWay = (L: CanalLayout, pos: number[]): 'pool' | 'sea' => {
  let e = L.inlet;
  for (let guard = 0; guard < 16; guard++) {
    const to = (L.edges[e] as CanalEdge).to;
    if (to.kind !== 'stone') return to.kind;
    const st = L.stones[to.n] as CanalStone;
    if (st.mouth !== undefined) return pos[to.n] === 0 ? 'pool' : 'sea';
    e = st.out[pos[to.n] ?? 0] as number;
  }
  return 'sea';
};
